import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useCallback, useEffect, useState } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display";
import { useModalTransition } from "@whoosh/design-system/src/components/overlay/Modal/ModalTransitionContext";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { Box, VStack } from "@whoosh/design-system/src/components/primitive";
import { SquareCard } from "@whoosh/features/src/Square/features/SquareCardEntry/types";

import { CardEntryErrorBanner } from "../../CardEntryErrorBanner";
import { SquareCardEntryModal } from "../../SquareCardEntryModal/SquareCardEntryModal";
import { ExistingSquareCardsList } from "./ExistingSquareCardsList";
import { SelectPaymentModalQuery } from "./__generated__/SelectPaymentModalQuery.graphql";

type Props = {
  customerId: string;
  isOpen: boolean;
  onClose: () => void;
  onSelectPaymentMethod: (paymentMethodId: string) => void;
  onAddNewCard: (card: SquareCard) => Promise<string>;
};

export const SelectPaymentModal = ({
  customerId,
  isOpen,
  onClose,
  onSelectPaymentMethod,
  onAddNewCard,
}: Props) => {
  const { showToast } = useToast();
  const { runAfterIdle } = useModalTransition();

  const [selectedCardId, setSelectedCardId] = useState<string | undefined>(
    undefined
  );

  const [queryRef, loadQuery] = useQueryLoader<SelectPaymentModalQuery>(
    SelectPaymentModalQueryString
  );

  const onConfirmSelection = useCallback(() => {
    if (selectedCardId) {
      onSelectPaymentMethod(selectedCardId);
    }
  }, [onSelectPaymentMethod, selectedCardId]);

  const fetchQuery = useCallback(() => {
    loadQuery({ customerId }, { fetchPolicy: "network-only" });
  }, [loadQuery, customerId]);

  useEffect(() => {
    fetchQuery();
  }, [fetchQuery]);

  const [isAddingNewCard, setIsAddingNewCard] = useState(false);
  const [
    isSelectModalClosingForCardEntry,
    setIsSelectModalClosingForCardEntry,
  ] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [shouldAutoOpenNewCard, setShouldAutoOpenNewCard] = useState(true);
  // Kept in the parent so card-entry cancellation and errors can decide
  // whether returning to the select modal is safe (an empty list normally
  // auto-redirects back into card entry).
  const [hasCardsOnFile, setHasCardsOnFile] = useState(false);

  const onNewCardAdded = (cardId: string) => {
    setErrorMessage(undefined);
    setSelectedCardId(cardId);
    setShouldAutoOpenNewCard(false);
    setIsAddingNewCard(false);
    fetchQuery();
    showToast({ description: t`Card added`, semanticStyle: "success" });
  };

  const onAddNewCardPress = useCallback(() => {
    if (isAddingNewCard || isSelectModalClosingForCardEntry) {
      return;
    }

    setErrorMessage(undefined);
    setIsSelectModalClosingForCardEntry(true);
    runAfterIdle(() => {
      setIsSelectModalClosingForCardEntry(false);
      setIsAddingNewCard(true);
    });
  }, [isAddingNewCard, isSelectModalClosingForCardEntry, runAfterIdle]);

  // Modal calls onClose whenever isOpen goes false — including our handoff
  // from select -> card entry. Swallow that so the parent does not unmount
  // this tree before SquareCardEntryModal opens.
  const handleSelectModalClose = useCallback(() => {
    if (isSelectModalClosingForCardEntry || isAddingNewCard) {
      return;
    }
    onClose();
  }, [isAddingNewCard, isSelectModalClosingForCardEntry, onClose]);

  const handleCardEntryCancel = useCallback(() => {
    setShouldAutoOpenNewCard(false);
    setIsAddingNewCard(false);
  }, []);

  const handleCardEntryError = useCallback(
    (message: string) => {
      setErrorMessage(message);
      setShouldAutoOpenNewCard(false);
      setIsAddingNewCard(false);
      fetchQuery();
    },
    [fetchQuery]
  );

  return (
    <>
      <StandardModal
        isOpen={isOpen && !isSelectModalClosingForCardEntry && !isAddingNewCard}
        onClose={handleSelectModalClose}
        upperTitle={t`Select Payment Method`}
        content={
          <VStack>
            <Box px={6}>
              <CardEntryErrorBanner message={errorMessage} />
            </Box>
            <Suspense fallback={<Loader />}>
              {queryRef ? (
                <PaymentSelectModalInner
                  queryRef={queryRef}
                  onClose={onClose}
                  selectedCardId={selectedCardId}
                  setSelectedCardId={(cardId) => {
                    setErrorMessage(undefined);
                    setSelectedCardId(cardId);
                  }}
                  onAddNewCardPress={onAddNewCardPress}
                  onHasCardsOnFileChange={setHasCardsOnFile}
                  shouldAutoOpenNewCard={shouldAutoOpenNewCard}
                />
              ) : null}
            </Suspense>
          </VStack>
        }
        footerContent={
          hasCardsOnFile ? (
            <Box pb={6}>
              <Button
                size="small"
                accessibilityLabel={t`Confirm Selected Payment Method Button`}
                testID="payment-methods-select-card-button"
                variant="main"
                onPress={onConfirmSelection}
                isDisabled={!selectedCardId}
                width="100%"
              >{t`Confirm Selected Payment Method`}</Button>
            </Box>
          ) : null
        }
      />
      {isAddingNewCard ? (
        <SquareCardEntryModal
          isOpen={isAddingNewCard}
          onCardEntryCancel={handleCardEntryCancel}
          onCardEntryError={handleCardEntryError}
          onCardEntryComplete={onAddNewCard}
          onCardEntrySaved={onNewCardAdded}
          loading={loading}
          setLoading={setLoading}
        />
      ) : null}
    </>
  );
};

type InnerProps = {
  selectedCardId?: string;
  setSelectedCardId: (cardId: string) => void;
  queryRef: PreloadedQuery<SelectPaymentModalQuery>;
  onAddNewCardPress: () => void;
  onClose: () => void;
  onHasCardsOnFileChange: (hasCardsOnFile: boolean) => void;
  shouldAutoOpenNewCard: boolean;
};

const PaymentSelectModalInner = ({
  onClose,
  queryRef,
  selectedCardId,
  onAddNewCardPress,
  setSelectedCardId,
  onHasCardsOnFileChange,
  shouldAutoOpenNewCard,
}: InnerProps) => {
  const data = usePreloadedQuery(SelectPaymentModalQueryString, queryRef);
  const customer = data.customer;

  const hasCardsOnFile = Boolean(
    customer?.squareCards?.edges?.length &&
      customer.squareCards.edges.length > 0
  );

  useEffect(() => {
    onHasCardsOnFileChange(hasCardsOnFile);
  }, [hasCardsOnFile, onHasCardsOnFileChange]);

  useEffect(() => {
    if (!hasCardsOnFile && shouldAutoOpenNewCard) {
      onAddNewCardPress();
    }
  }, [hasCardsOnFile, onAddNewCardPress, shouldAutoOpenNewCard]);

  return (
    <VStack bg="surface-primary" px={6} borderRadius="6" gap={4}>
      <ExistingSquareCardsList
        customerRef={customer}
        onAddNewCard={onAddNewCardPress}
        onCancel={onClose}
        selectedCardId={selectedCardId}
        setSelectedCardId={setSelectedCardId}
      />
    </VStack>
  );
};

const SelectPaymentModalQueryString = graphql`
  query SelectPaymentModalQuery($customerId: ID!) {
    customer: node(id: $customerId) {
      ... on Customer {
        ...ExistingSquareCardsList_customer
        id
        squareCards(includeUserCards: true, enabled: true) {
          edges {
            node {
              id
              enabled
              cardBrand
              expMonth
              expYear
              lastFour
            }
          }
        }
      }
    }
  }
`;
